import { useMemo, useState } from 'react'
import { Drawer, Input, VStack, HStack, Text, Badge, Portal, CloseButton } from '@chakra-ui/react';
import { constituencyFor } from './lobbyScheduleUtils'
import type { ConstituencyStatus, ScheduleAttendeeDto, ScheduleLegislatorRowDto } from '../../../types/lobbyDay'

const CONSTITUENCY: Record<ConstituencyStatus, { label: string; color: string }> = {
  Constituent: { label: 'constituent', color: 'green' },
  NotConstituent: { label: 'not in district', color: 'red' },
  Unknown: { label: 'unverified', color: 'yellow' },
}

interface AttendeePickerSheetProps {
  isOpen: boolean
  onClose: () => void
  attendees: ScheduleAttendeeDto[]
  legislator: ScheduleLegislatorRowDto | undefined
  assignedAttendeeIds: Set<string>
  onSelect: (attendeeId: string) => void
}

export function AttendeePickerSheet({
  isOpen, onClose, attendees, legislator, assignedAttendeeIds, onSelect,
}: AttendeePickerSheetProps) {
  const [query, setQuery] = useState('')
  const available = useMemo(() => {
    const q = query.trim().toLowerCase()
    return attendees
      .filter((a) => !assignedAttendeeIds.has(a.attendeeId))
      .filter((a) => !q || a.fullName.toLowerCase().includes(q))
  }, [attendees, assignedAttendeeIds, query])

  return (
    <Drawer.Root open={isOpen} placement='bottom' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content borderTopRadius="xl" maxH="85vh" pb="env(safe-area-inset-bottom, 0px)">
            <Drawer.CloseTrigger asChild><CloseButton size="sm" /></Drawer.CloseTrigger>
            <Drawer.Header borderBottomWidth="1px">Add attendee</Drawer.Header>
            <Drawer.Body>
              <Input placeholder="Search attendees…" value={query} onChange={(e) => setQuery(e.target.value)} mb={3} mt={2} />
              <VStack gap={1} align="stretch" pb={2}>
                {available.length === 0 && <Text fontSize="sm" color="gray.400">No attendees available</Text>}
                {available.map((a) => {
                  const status = legislator ? constituencyFor(a, legislator) : 'Unknown'
                  const badge = CONSTITUENCY[status]
                  return (
                    <HStack
                      textAlign="left"
                      justify="space-between"
                      minH="48px"
                      px={3}
                      py={2}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="gray.200"
                      _active={{ bg: 'gray.50' }}
                      asChild><button key={a.attendeeId} onClick={() => { onSelect(a.attendeeId); onClose() }}>
                            <Text fontSize="sm" lineClamp={1}>{a.fullName}</Text>
                            <Badge colorPalette={badge.color} fontSize="2xs" flexShrink={0}>{badge.label}</Badge>
                          </button></HStack>
                  );
                })}
              </VStack>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>

      </Portal>
    </Drawer.Root>
  );
}
